import type { FolderListPageResponseDatagramBuf } from "~/classes/generated/response"

import { PropertyId } from "~/classes/entity"
import { DatagramBuf, DatagramErrorTypeBuf } from "~/classes/generated/datagram"
import { FolderListRequestDatagramBuf, RequestDatagramBuf } from "~/classes/generated/request"
import { DatagramBufferBuilder } from "~/utils/flatbuffers/builder"
import { addRequestIdToBuf, parseDatagramError, parseRequestId, parseVector } from "~/utils/flatbuffers/datagram"
import { addEntityPropertySelectionToBuf } from "~/utils/flatbuffers/entity"
import { addFileToBuf, parseFile } from "~/utils/flatbuffers/file"
import { addFilePropertyIdToBuf, parseFileId } from "~/utils/flatbuffers/id"
import { maybe } from "~/utils/helpers"

// We log the start time of every folder list request
const startTimes: Record<string, number> = {}
const console = useLogger("file/list", theme.colors.pink.hex)

export function sendFolderListRequests(requests: FolderListRequestDatagram[]) {
  const { sendWorkerDatagramBuf } = useCache()

  for (const request of requests) {
    startTimes[request.requestId] = Date.now()
  }

  const fbb = new DatagramBufferBuilder(64)
  const requestOffsets: number[] = []

  for (const request of requests) {
    const folder = addFileToBuf(fbb, request.folder)
    let selection, pathSelection
    if (request.selection)
      selection = addEntityPropertySelectionToBuf(fbb, request.selection)
    if (request.pathSelection)
      pathSelection = addEntityPropertySelectionToBuf(fbb, request.pathSelection)
    const requestId = addRequestIdToBuf(fbb, request.requestId)
    const sort = request.sort || PropertyId.FILE_CREATED_AT
    const sortBuf = addFilePropertyIdToBuf(fbb, sort)
    FolderListRequestDatagramBuf.startFolderListRequestDatagramBuf(fbb)
    FolderListRequestDatagramBuf.addRequestId(fbb, requestId)
    FolderListRequestDatagramBuf.addFolder(fbb, folder)
    if (selection)
      FolderListRequestDatagramBuf.addSelection(fbb, selection)
    if (pathSelection)
      FolderListRequestDatagramBuf.addPathSelection(fbb, pathSelection)
    if (request.desc !== undefined)
      FolderListRequestDatagramBuf.addDesc(fbb, request.desc)
    FolderListRequestDatagramBuf.addPageSize(fbb, request.pageSize || 50) // FIXME: update this
    FolderListRequestDatagramBuf.addOffline(fbb, request.offline)
    FolderListRequestDatagramBuf.addSort(fbb, sortBuf)
    const listRequest = FolderListRequestDatagramBuf.endFolderListRequestDatagramBuf(fbb)

    RequestDatagramBuf.startRequestDatagramBuf(fbb)
    RequestDatagramBuf.addFolderListRequest(fbb, listRequest)
    requestOffsets.push(RequestDatagramBuf.endRequestDatagramBuf(fbb))
  }

  const requestBuffers = DatagramBuf.createRequestVector(fbb, requestOffsets)
  DatagramBuf.startDatagramBuf(fbb)
  DatagramBuf.addRequest(fbb, requestBuffers)
  fbb.finish(DatagramBuf.endDatagramBuf(fbb))
  sendWorkerDatagramBuf(fbb.asUint8Array())
}

/** Parse a Flatbuffers representation of a response */
export function handleFolderListResponseDatagramBuf(datagram: FolderListPageResponseDatagramBuf) {
  const files = parseVector(i => datagram.files(i)!, datagram.filesLength()).map(parseFile)
  const path = parseVector(i => datagram.path(i)!, datagram.pathLength()).map(parseFile)

  // Now, let's go ahead and beef up all of our files with the path data
  // so that any further handlers don't need to do this work again.
  // TODO: remove this or move to response method as this breaks assumptions about
  // the datagram
  for (const file of files)
    file.path = path

  const response: FolderListPageResponseDatagram = {
    folderId: parseFileId(datagram.folderId()!),
    requestId: parseRequestId(datagram.requestId()!),
    error: maybe(parseDatagramError)(datagram.error()),
    files,
    path,
    offline: datagram.offline(),
  }

  const startTime = startTimes[response.requestId]
  if (startTime) {
    console.debug("Folder list latency (ms):", Date.now() - startTime)
  }

  onFolderListPageResponse(response)
}

function onFolderListPageResponse(
  datagram: FolderListPageResponseDatagram,
) {
  const { emitInterfaceEvent, emitEntityEvent } = useEvents()

  emitInterfaceEvent("folderListPageResponse", datagram, datagram.requestId)

  if (datagram.error)
    return onFolderListPageErrored(datagram.error, datagram.requestId, datagram.folderId, datagram.offline)

  if (!datagram.files || !datagram.path)
    throw new Error("No files in response")

  // send all of our file events based on the contents of this page
  const folderId = datagram.folderId
  for (const file of datagram.files) {
    emitEntityEvent("fileProperties", file.id, file, datagram.offline)
    emitEntityEvent("childProperties", folderId, file, datagram.offline)
  }
}

function onFolderListPageErrored(
  error: DatagramError,
  _requestId: string,
  folderId: string,
  offline: boolean,
) {
  const { navigateToSelfHome, currentRootFolder } = useNavigation()
  const { notifyDatagramError } = useAlerts()

  // NOTE: We only want to send these errors if this folder request corresponds
  // with the user's current directory, as otherwise the user might get too many
  // notifications for non-userspace jobs.
  if (folderId === currentRootFolder.value?.id) {
    if (error.type === DatagramErrorTypeBuf.FileNotFound && !offline) {
      // The file was not found, we should mention it wasn't found then redirect
      // to the home directory.
      navigateToSelfHome()
      return notifyDatagramError(error, "That folder doesn't seem to exist anymore")
    }
    else if (error.type === DatagramErrorTypeBuf.FileNotFound && offline)
      return console.info("Offline folder list not found, waiting for server response")
    else
      return notifyDatagramError(error, "There was a problem listing the folder")
  }

  return console.warn("Folder list errored", error)
}
